import { useEffect, useState, useRef } from "react";
import api from "../lib/api";
import PageHeader, { PageBody, Card, Badge, useFetch } from "../components/Shared";
import {
  PaperPlaneTilt, ChatCircle, Plus, Lightning, ArrowsClockwise, FastForward,
  CheckCircle, CheckCircle as CheckRead, Phone, VideoCamera, DotsThreeVertical,
  Sparkle, Trash,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const WHATSAPP_BG =
  "linear-gradient(rgba(229, 221, 213, 0.85), rgba(229, 221, 213, 0.85)), url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='60' height='60' viewBox='0 0 60 60'%3E%3Cpath fill='%23000' fill-opacity='0.03' d='M30 0 L60 30 L30 60 L0 30 Z'/%3E%3C/svg%3E\")";

// Aplana errores de FastAPI (422 viene como array de objetos {type,loc,msg,...}) a string seguro
function humanizeError(err) {
  const d = err?.response?.data?.detail;
  if (!d) return err?.message || "Error";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) {
    return d.map((it) => {
      if (typeof it === "string") return it;
      const loc = Array.isArray(it.loc) ? it.loc.slice(-1)[0] : "";
      return `${loc ? loc + ": " : ""}${it.msg || JSON.stringify(it)}`;
    }).join(" · ");
  }
  if (typeof d === "object") return d.msg || JSON.stringify(d);
  return String(d);
}

export default function WhatsAppSimulator() {
  const { data: convs, reload: reloadConvs } = useFetch(() => api.get("/whatsapp/conversations").then((r) => r.data), []);
  const { data: users, reload: reloadUsers } = useFetch(() => api.get("/users", { params: { limit: 200 } }).then((r) => r.data), []);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [thread, setThread] = useState(null);
  const [pipelineState, setPipelineState] = useState(null);
  const [pendingJobs, setPendingJobs] = useState([]);
  const [body, setBody] = useState("");
  const [mode, setMode] = useState("simulate-inbound"); // realistic default
  const [busy, setBusy] = useState(false);
  const [scenarios, setScenarios] = useState([]);
  const messagesRef = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => {
    api.get("/whatsapp/simulator/scenarios").then((r) => setScenarios(r.data.scenarios || []));
  }, []);

  useEffect(() => {
    if (!selectedUserId) return;
    loadAll();
    // poll every 3s to catch IA replies + new jobs
    pollRef.current = setInterval(loadThread, 3000);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line
  }, [selectedUserId]);

  const loadAll = async () => {
    await Promise.all([loadThread(), loadPipelineState(), loadPendingJobs()]);
  };
  const loadThread = async () => {
    try {
      const { data } = await api.get(`/whatsapp/conversations/${selectedUserId}`);
      setThread(data);
      setTimeout(() => messagesRef.current?.scrollTo({ top: messagesRef.current.scrollHeight, behavior: "smooth" }), 80);
    } catch { /* */ }
  };
  const loadPipelineState = async () => {
    try { const { data } = await api.get(`/whatsapp/simulator/pipeline-state/${selectedUserId}`); setPipelineState(data); }
    catch { /* */ }
  };
  const loadPendingJobs = async () => {
    try { const { data } = await api.get(`/whatsapp/simulator/pending-jobs/${selectedUserId}`); setPendingJobs(data || []); }
    catch { /* */ }
  };

  const send = async () => {
    if (!body.trim() || !selectedUserId) return;
    setBusy(true);
    try {
      if (mode === "outbound") {
        await api.post("/whatsapp/send", { user_id: selectedUserId, body });
      } else {
        await api.post("/whatsapp/simulate-inbound", { user_id: selectedUserId, body });
      }
      setBody("");
      // Wait 1.5s to let IA respond, then refresh
      setTimeout(loadAll, 1500);
      reloadConvs();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error enviando");
    } finally { setBusy(false); }
  };

  const startScenario = async (key) => {
    setBusy(true);
    try {
      const { data } = await api.post("/whatsapp/simulator/scenarios/start", { key });
      toast.success(`Escenario creado · ${data.public_id}`);
      reloadUsers(); reloadConvs();
      setTimeout(() => setSelectedUserId(data.user_id), 600);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setBusy(false); }
  };

  const fireJob = async (jobId) => {
    try {
      await api.post(`/whatsapp/simulator/fire-job/${jobId}`);
      toast.success("Job disparado");
      setTimeout(loadAll, 1200);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const fireAllPending = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/whatsapp/simulator/fire-all-pending/${selectedUserId}`);
      toast.success(`${data.fired.length} jobs disparados · ${data.failed.length} fallos`);
      setTimeout(loadAll, 1500);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setBusy(false); }
  };

  const timeTravel = async (days) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/whatsapp/simulator/time-travel/${selectedUserId}`, { days });
      const label = days === 1 ? "1 día" : days === 7 ? "1 semana" : `${days} días`;
      toast.success(`⏩ Avancé ${label} · ${data.successful_count}/${data.events_count} eventos disparados`);
      setTimeout(loadAll, 2000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setBusy(false); }
  };

  const advancePipeline = async (slug) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/whatsapp/simulator/advance-pipeline/${selectedUserId}`, { pipeline_slug: slug });
      toast.success(`${data.from_stage || "—"} → ${data.to_stage}`);
      setTimeout(loadAll, 1500);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setBusy(false); }
  };

  const runIAAnalysis = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/intelligence/analyze/${selectedUserId}`, {});
      toast.success(`Análisis IA · ${(data.automated_actions || []).length} acciones`);
      setTimeout(loadAll, 1000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setBusy(false); }
  };

  // Acciones operativas del Ambassador pipeline contextuales al stage actual
  const ambassadorAction = async (path, successLabel) => {
    setBusy(true);
    try {
      // Atajos: para interview/schedule e induction/schedule mandamos un BOOKING LINK
      // en lugar de auto-agendar — el usuario elige el horario en la página pública.
      if (path === "interview/schedule") {
        const { data } = await api.post("/calendar/booking-token/send", { user_id: selectedUserId, kind: "interview", duration_min: 45 });
        toast.success("Link de entrevista enviado al usuario");
        if (data?.booking_url) navigator.clipboard?.writeText(data.booking_url).catch(() => {});
        setTimeout(loadAll, 1500);
        return;
      }
      if (path === "induction/schedule") {
        const { data } = await api.post("/calendar/booking-token/send", { user_id: selectedUserId, kind: "induction", duration_min: 60 });
        toast.success("Link de capacitación enviado al usuario");
        if (data?.booking_url) navigator.clipboard?.writeText(data.booking_url).catch(() => {});
        setTimeout(loadAll, 1500);
        return;
      }
      // Bodies por defecto (algunos endpoints exigen payload con campos required)
      const bodies = {
        "score": {},
        "video/request": {},
        "video/record": { video_url: "mock://simulator/video-30s.mp4", ai_notes: "Recibido vía simulator" },
        "agreement/send": {},
        "agreement/sign": {},
        "induction/complete": {},
        "activate": {},
      };
      await api.post(`/ambassador/${path}/${selectedUserId}`, bodies[path] || {});
      toast.success(successLabel);
      setTimeout(loadAll, 1500);
    } catch (e) { toast.error(humanizeError(e)); } finally { setBusy(false); }
  };
  const interviewSubmit = async (decision) => {
    setBusy(true);
    try {
      // Backend espera "approved" / "rejected"
      const normalized = decision === "approve" ? "approved" : "rejected";
      await api.post(`/ambassador/interview/submit/${selectedUserId}`, { decision: normalized });
      toast.success(normalized === "approved" ? "Entrevista aprobada" : "Entrevista rechazada");
      setTimeout(loadAll, 1500);
    } catch (e) { toast.error(humanizeError(e)); } finally { setBusy(false); }
  };
  // Acciones del Journey V2 (Aspirante a Embajador) contextuales a la etapa
  const v2Action = async (action) => {
    setBusy(true);
    try {
      const uid = selectedUserId;
      if (action === "extract-evidence") {
        const { data } = await api.post(`/aspirants/${uid}/extract-evidence`);
        toast.success(`Evidencia IA · ${data.created} evidencias · ${data.next_actions_created} próximas acciones`);
      } else if (action === "schedule-interview") {
        const when = new Date(Date.now() + 2 * 24 * 3600 * 1000).toISOString();
        await api.post(`/aspirants/${uid}/interview/schedule`, { scheduled_at: when });
        toast.success("Entrevista agendada (+2 días) · movido a etapa entrevista");
      } else if (action === "approved") {
        await api.post(`/aspirants/${uid}/interview/result`, { result: "APPROVED", reason: "Simulador · aprobado" });
        toast.success("Aprobado → Candidato");
      } else if (action === "not-yet") {
        const desc = window.prompt("Acción específica para 'Todavía no':", "Completar 3 referidos verificados");
        if (!desc || !desc.trim()) { setBusy(false); return; }
        await api.post(`/aspirants/${uid}/interview/result`, { result: "NOT_YET", reason: "Simulador", next_action_description: desc.trim() });
        toast.success("Todavía no → próxima acción creada");
      } else if (action === "not-approved") {
        await api.post(`/aspirants/${uid}/interview/result`, { result: "NOT_APPROVED", reason: "Simulador · no aprobado" });
        toast.success("No aprobado → sigue en comunidad");
      } else if (action === "contract-signed") {
        await api.post(`/aspirants/${uid}/formalization`, { status: "CONTRACT_SIGNED", contract_reference: "SIM-CONTRACT-001" });
        toast.success("Contrato firmado");
      } else if (action === "activate") {
        await api.post(`/aspirants/${uid}/formalization`, { status: "AMBASSADOR_ACTIVE" });
        toast.success("¡Embajador activo!");
      }
      setTimeout(loadAll, 1500);
    } catch (e) { toast.error(humanizeError(e)); } finally { setBusy(false); }
  };
  // Mapa stage → botones a mostrar
  const AMBASSADOR_ACTIONS = {
    applicant: [{ label: "Evaluar (score)", path: "score", success: "Postulante evaluado" }],
    scored: [{ label: "Evaluar de nuevo", path: "score", success: "Re-evaluado" }],
    priority_high: [{ label: "Pedir video", path: "video/request", success: "Video solicitado" }],
    priority_medium: [{ label: "Pedir video", path: "video/request", success: "Video solicitado" }],
    video_pending: [{ label: "Marcar video recibido", path: "video/record", success: "Video registrado" }],
    video_received: [{ label: "Enviar link de entrevista", path: "interview/schedule", success: "Link enviado" }],
    interview_approved: [{ label: "Enviar acuerdo", path: "agreement/send", success: "Acuerdo enviado" }],
    agreement_pending: [{ label: "Marcar firmado", path: "agreement/sign", success: "Acuerdo firmado" }],
    signed: [{ label: "Enviar link de capacitación", path: "induction/schedule", success: "Link enviado" }],
    induction_pending: [{ label: "Inducción completa", path: "induction/complete", success: "Inducción completada" }],
    induction_completed: [{ label: "Activar embajadora", path: "activate", success: "Embajadora activa" }],
  };

  const usersList = (users?.items || []).filter((u) => u.whatsapp);

  return (
    <>
      <PageHeader
        label="End-to-end demo"
        title="Simulador WhatsApp"
        description="Mira el flujo completo de los pipelines como ocurrirá en WhatsApp real. La IA responde como lo hará en producción. Usa los botones del panel admin para acelerar el tiempo y disparar jobs programados."
      />
      <PageBody>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 h-[calc(100vh-200px)]">
          {/* Conversations + Scenarios */}
          <Card className="lg:col-span-3 overflow-hidden flex flex-col">
            <div className="px-4 py-3 border-b border-carbon-200">
              <div className="section-label mb-2">Escenarios demo</div>
              {scenarios.map((s) => (
                <button
                  key={s.key}
                  onClick={() => startScenario(s.key)}
                  data-testid={`scenario-${s.key}`}
                  className="w-full mb-1.5 text-left text-xs border border-carbon-200 hover:border-terracotta rounded p-2 transition-colors"
                  disabled={busy}
                >
                  <div className="flex items-center gap-1 font-medium">
                    <Plus size={12} className="text-terracotta" /> {s.label}
                  </div>
                  <div className="text-carbon-500 mt-0.5 leading-relaxed">{s.description}</div>
                </button>
              ))}
            </div>
            <div className="px-4 py-2 border-b border-carbon-200">
              <div className="section-label">Conversaciones</div>
            </div>
            <div className="flex-1 overflow-y-auto scrollbar-thin">
              {usersList.length === 0 && <div className="p-4 text-xs text-carbon-400">Crea un escenario demo arriba.</div>}
              {usersList.map((u) => {
                const conv = (convs || []).find((c) => c.user_id === u.id);
                return (
                  <button
                    key={u.id}
                    onClick={() => setSelectedUserId(u.id)}
                    data-testid={`conv-item-${u.id}`}
                    className={`w-full text-left px-4 py-2.5 border-b border-carbon-100 hover:bg-page transition-colors ${selectedUserId === u.id ? "bg-page border-l-2 border-l-terracotta" : ""}`}
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-forest/15 text-forest flex items-center justify-center text-xs font-bold">
                        {u.name?.charAt(0) || "?"}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-1">
                          <span className="font-medium text-sm truncate">{u.name}</span>
                          {conv?.unread_count > 0 && <span className="text-[10px] bg-terracotta text-white rounded-full px-1.5">{conv.unread_count}</span>}
                        </div>
                        <div className="text-[10px] text-carbon-500 font-mono truncate">{u.whatsapp}</div>
                        {conv?.last_message_preview && (
                          <div className="text-xs text-carbon-400 truncate mt-0.5">{conv.last_message_preview}</div>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </Card>

          {/* Phone-style WhatsApp chat */}
          <div className="lg:col-span-6 flex items-center justify-center">
            <div className="w-full max-w-[420px] h-full bg-carbon-900 rounded-[28px] p-2 shadow-2xl flex flex-col" data-testid="phone-frame">
              <div className="bg-surface rounded-[22px] flex-1 flex flex-col overflow-hidden">
                {!selectedUserId ? (
                  <div className="flex-1 flex items-center justify-center text-carbon-400">
                    <div className="text-center px-6">
                      <ChatCircle size={48} className="mx-auto mb-3 text-carbon-300" />
                      <div className="text-sm">Selecciona o crea una conversación</div>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* WhatsApp-style header */}
                    <div className="px-3 py-2 bg-[#075e54] text-white flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center font-bold text-sm">
                        {thread?.user?.name?.charAt(0) || "?"}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate">{thread?.user?.name || "—"}</div>
                        <div className="text-[10px] opacity-80 truncate">{thread?.user?.whatsapp}</div>
                      </div>
                      <VideoCamera size={18} weight="regular" className="opacity-80" />
                      <Phone size={18} weight="regular" className="opacity-80" />
                      <DotsThreeVertical size={18} weight="bold" className="opacity-80" />
                    </div>
                    {/* Messages */}
                    <div ref={messagesRef} className="flex-1 overflow-y-auto px-3 py-3 scrollbar-thin"
                         style={{ backgroundImage: WHATSAPP_BG, backgroundSize: "60px 60px" }}>
                      <div className="space-y-1.5">
                        {(thread?.messages || []).map((m) => (
                          <div key={m.id} className={`flex ${m.direction === "outbound" ? "justify-end" : "justify-start"}`}>
                            <div className={`max-w-[80%] px-2.5 py-1.5 rounded-lg text-sm shadow-sm relative ${
                              m.direction === "outbound"
                                ? "bg-[#dcf8c6] text-carbon-900"
                                : "bg-white text-carbon-900"
                            }`}>
                              {m.ai_origin && <div className="text-[9px] uppercase tracking-wider text-terracotta mb-0.5 font-semibold">IA</div>}
                              <div className="whitespace-pre-wrap break-words pr-12">{m.body}</div>
                              <div className="text-[9px] text-carbon-500 mt-0.5 flex items-center gap-0.5 justify-end">
                                {new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                {m.direction === "outbound" && (
                                  <span className="ml-1">
                                    {m.delivery_status === "read"
                                      ? <span className="text-[#34b7f1]">✓✓</span>
                                      : m.delivery_status === "delivered"
                                        ? <span className="text-carbon-500">✓✓</span>
                                        : <span className="text-carbon-500">✓</span>}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                        {(!thread?.messages || thread.messages.length === 0) && (
                          <div className="text-center text-carbon-500 text-xs py-8">Aún sin mensajes</div>
                        )}
                      </div>
                    </div>
                    {/* Input bar */}
                    <div className="border-t border-carbon-200 p-2 bg-[#f0f0f0] flex items-center gap-2">
                      <input
                        value={body}
                        onChange={(e) => setBody(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                        placeholder={mode === "outbound" ? "Operador (HM) escribe..." : "Usuario responde..."}
                        data-testid="chat-input"
                        className="flex-1 bg-white border border-carbon-200 rounded-full px-4 py-2 text-sm focus:outline-none focus:border-forest"
                      />
                      <button onClick={send} disabled={busy || !body.trim()} data-testid="chat-send-btn"
                        className={`rounded-full w-10 h-10 flex items-center justify-center text-white ${mode === "outbound" ? "bg-terracotta" : "bg-forest"} disabled:opacity-60`}>
                        <PaperPlaneTilt size={16} weight="fill" />
                      </button>
                    </div>
                    <div className="px-3 py-1.5 bg-[#f0f0f0] border-t border-carbon-200 flex items-center justify-center gap-2 text-[10px]">
                      <button onClick={() => setMode("simulate-inbound")} data-testid="mode-inbound"
                        className={`px-2 py-0.5 rounded ${mode === "simulate-inbound" ? "bg-forest text-white" : "text-carbon-500 hover:bg-white"}`}>
                        Hablar como usuario
                      </button>
                      <button onClick={() => setMode("outbound")} data-testid="mode-outbound"
                        className={`px-2 py-0.5 rounded ${mode === "outbound" ? "bg-terracotta text-white" : "text-carbon-500 hover:bg-white"}`}>
                        Hablar como operador HM
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Admin actions panel */}
          <Card className="lg:col-span-3 overflow-hidden flex flex-col" testid="admin-panel">
            <div className="px-4 py-3 border-b border-carbon-200">
              <div className="section-label">Panel admin · acelerar tiempo</div>
            </div>
            <div className="flex-1 overflow-y-auto scrollbar-thin p-4 space-y-4">
              {!selectedUserId ? (
                <div className="text-xs text-carbon-400">Selecciona una conversación.</div>
              ) : (
                <>
                  {/* Time travel */}
                  <div className="border border-terracotta/30 bg-terracotta/5 rounded p-2.5">
                    <div className="section-label mb-2 text-terracotta">⏩ Time travel</div>
                    <div className="text-[10px] text-carbon-700 mb-2 leading-snug">
                      Avanza el tiempo y dispara EN ORDEN todos los jobs que ocurrirían en ese período (incluyendo check-ins y ciclos IA).
                    </div>
                    <div className="grid grid-cols-3 gap-1">
                      <button onClick={() => timeTravel(1)} disabled={busy} data-testid="tt-1d"
                        className="bg-terracotta text-white text-[10px] px-1 py-1.5 rounded hover:bg-terracotta-hover disabled:opacity-60 font-medium">
                        +1 día
                      </button>
                      <button onClick={() => timeTravel(7)} disabled={busy} data-testid="tt-7d"
                        className="bg-terracotta text-white text-[10px] px-1 py-1.5 rounded hover:bg-terracotta-hover disabled:opacity-60 font-medium">
                        +1 sem
                      </button>
                      <button onClick={() => timeTravel(30)} disabled={busy} data-testid="tt-30d"
                        className="bg-terracotta text-white text-[10px] px-1 py-1.5 rounded hover:bg-terracotta-hover disabled:opacity-60 font-medium">
                        +1 mes
                      </button>
                    </div>
                  </div>

                  {/* Pipeline state */}
                  {pipelineState?.memberships?.length > 0 && (
                    <div>
                      <div className="section-label mb-1">Pipeline actual</div>
                      {pipelineState.memberships.map((m) => {
                        const ambActions = m.pipeline_slug === "ambassador" ? (AMBASSADOR_ACTIONS[m.current_stage_key] || []) : [];
                        const isInterviewPending = m.pipeline_slug === "ambassador" && m.current_stage_key === "interview_pending";
                        const isV2 = m.pipeline_slug === "aspirantes_v2";
                        const v2Stage = m.current_stage_key;
                        return (
                          <div key={m.pipeline_id} className="border border-carbon-200 rounded p-2 mb-2">
                            <div className="text-xs font-medium mb-1">{m.pipeline_name}</div>
                            <Badge tone="terracotta">{m.current_stage_name || m.current_stage_key}</Badge>
                            {/* Acciones del Journey V2 (Aspirante a Embajador) */}
                            {isV2 && (
                              <div className="mt-2 space-y-1.5" data-testid="v2-actions">
                                <button
                                  onClick={() => v2Action("extract-evidence")}
                                  disabled={busy}
                                  data-testid="v2-extract-evidence"
                                  className="w-full bg-forest text-white text-xs px-2 py-1.5 rounded flex items-center justify-center gap-1 hover:opacity-90 disabled:opacity-60"
                                >
                                  <Sparkle size={12} /> Extraer evidencia con IA
                                </button>
                                {["explorando", "participando", "evidencia"].includes(v2Stage) && (
                                  <button
                                    onClick={() => v2Action("schedule-interview")}
                                    disabled={busy}
                                    data-testid="v2-schedule-interview"
                                    className="w-full bg-terracotta text-white text-xs px-2 py-1.5 rounded flex items-center justify-center gap-1 hover:bg-terracotta-hover disabled:opacity-60"
                                  >
                                    <Lightning size={12} /> Agendar entrevista
                                  </button>
                                )}
                                {v2Stage === "entrevista" && (
                                  <div className="border-t border-carbon-200 pt-1.5">
                                    <div className="text-[10px] text-carbon-500 mb-1">Decisión de entrevista (Admin HM)</div>
                                    <div className="grid grid-cols-1 gap-1">
                                      <button onClick={() => v2Action("approved")} disabled={busy} data-testid="v2-interview-approved"
                                        className="bg-forest text-white text-xs px-2 py-1.5 rounded hover:opacity-90 disabled:opacity-60">
                                        Aprobado → Candidato
                                      </button>
                                      <button onClick={() => v2Action("not-yet")} disabled={busy} data-testid="v2-interview-notyet"
                                        className="bg-terracotta text-white text-xs px-2 py-1.5 rounded hover:bg-terracotta-hover disabled:opacity-60">
                                        Todavía no (+ acción)
                                      </button>
                                      <button onClick={() => v2Action("not-approved")} disabled={busy} data-testid="v2-interview-notapproved"
                                        className="bg-signal text-white text-xs px-2 py-1.5 rounded hover:opacity-90 disabled:opacity-60">
                                        No aprobado
                                      </button>
                                    </div>
                                  </div>
                                )}
                                {v2Stage === "candidato" && (
                                  <div className="border-t border-carbon-200 pt-1.5">
                                    <div className="text-[10px] text-carbon-500 mb-1">Formalización</div>
                                    <div className="grid grid-cols-1 gap-1">
                                      <button onClick={() => v2Action("contract-signed")} disabled={busy} data-testid="v2-formalize-contract"
                                        className="bg-carbon-900 text-white text-xs px-2 py-1.5 rounded hover:bg-carbon-800 disabled:opacity-60">
                                        Marcar contrato firmado
                                      </button>
                                      <button onClick={() => v2Action("activate")} disabled={busy} data-testid="v2-formalize-activate"
                                        className="bg-forest text-white text-xs px-2 py-1.5 rounded hover:opacity-90 disabled:opacity-60">
                                        Activar Embajador
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                            {/* Acciones contextuales del Ambassador pipeline */}
                            {ambActions.map((a) => (
                              <button
                                key={a.path}
                                onClick={() => ambassadorAction(a.path, a.success)}
                                disabled={busy}
                                data-testid={`amb-action-${a.path.replace(/\//g, "-")}`}
                                className="mt-2 w-full bg-terracotta text-white text-xs px-2 py-1.5 rounded flex items-center justify-center gap-1 hover:bg-terracotta-hover disabled:opacity-60"
                              >
                                <Lightning size={12} /> {a.label}
                              </button>
                            ))}
                            {isInterviewPending && (
                              <div className="mt-2 grid grid-cols-2 gap-1">
                                <button
                                  onClick={() => interviewSubmit("approve")}
                                  disabled={busy}
                                  data-testid="amb-action-interview-approve"
                                  className="bg-forest text-white text-xs px-2 py-1.5 rounded hover:opacity-90 disabled:opacity-60"
                                >
                                  Aprobar entrevista
                                </button>
                                <button
                                  onClick={() => interviewSubmit("reject")}
                                  disabled={busy}
                                  data-testid="amb-action-interview-reject"
                                  className="bg-signal text-white text-xs px-2 py-1.5 rounded hover:opacity-90 disabled:opacity-60"
                                >
                                  Rechazar
                                </button>
                              </div>
                            )}
                            <button
                              onClick={() => advancePipeline(m.pipeline_slug)}
                              disabled={busy}
                              data-testid={`advance-${m.pipeline_slug}`}
                              className="mt-2 w-full bg-carbon-900 text-white text-xs px-2 py-1.5 rounded flex items-center justify-center gap-1 hover:bg-carbon-800 disabled:opacity-60"
                            >
                              <FastForward size={12} /> Avanzar al siguiente stage
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Scores */}
                  {pipelineState?.user && (
                    <div>
                      <div className="section-label mb-1">Scores IA</div>
                      <div className="grid grid-cols-2 gap-1 text-[10px]">
                        <ScoreMini label="Engagement" value={pipelineState.user.engagement_score} />
                        <ScoreMini label="Leadership" value={pipelineState.user.leadership_score} />
                        <ScoreMini label="Embajador" value={pipelineState.user.ambassador_score} max={100} />
                        <ScoreMini label="Moderation" value={pipelineState.user.moderation_risk_score} risk />
                      </div>
                      {(pipelineState.user.tags || []).length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {pipelineState.user.tags.map((t, i) => <Badge key={i}>{t}</Badge>)}
                        </div>
                      )}
                      <button onClick={runIAAnalysis} disabled={busy} data-testid="run-ia-btn"
                        className="mt-2 w-full bg-terracotta text-white text-xs px-2 py-1.5 rounded flex items-center justify-center gap-1 hover:bg-terracotta-hover disabled:opacity-60">
                        <Sparkle size={12} /> Re-analizar con IA
                      </button>
                    </div>
                  )}

                  {/* Pending jobs */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <div className="section-label">Jobs programados · {pendingJobs.length}</div>
                      {pendingJobs.length > 0 && (
                        <button onClick={fireAllPending} disabled={busy} data-testid="fire-all-btn" className="text-[10px] text-terracotta hover:underline">
                          Disparar todos
                        </button>
                      )}
                    </div>
                    {pendingJobs.length === 0 && <div className="text-xs text-carbon-400">Sin jobs pendientes</div>}
                    <div className="space-y-1 max-h-[280px] overflow-y-auto scrollbar-thin">
                      {pendingJobs.map((j) => (
                        <div key={j.id} className="border border-carbon-200 rounded p-2 text-xs">
                          <div className="font-medium truncate mb-0.5">{j.name}</div>
                          <div className="text-[10px] text-carbon-500 truncate font-mono">{j.job_type}</div>
                          {j.payload?._human_summary && <div className="text-[10px] text-carbon-500 truncate">{j.payload._human_summary}</div>}
                          <button onClick={() => fireJob(j.id)} disabled={busy}
                            data-testid={`fire-job-${j.id}`}
                            className="mt-1 w-full bg-forest text-white text-[10px] px-2 py-1 rounded flex items-center justify-center gap-1 hover:opacity-90 disabled:opacity-60">
                            <Lightning size={10} /> Disparar ahora
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>

                  <button onClick={loadAll} data-testid="refresh-btn"
                    className="w-full bg-carbon-200 text-carbon-900 text-xs px-2 py-1.5 rounded flex items-center justify-center gap-1 hover:bg-carbon-300">
                    <ArrowsClockwise size={12} /> Actualizar
                  </button>
                </>
              )}
            </div>
          </Card>
        </div>
      </PageBody>
    </>
  );
}

function ScoreMini({ label, value, max = 1, risk = false }) {
  const v = Number(value || 0);
  const display = max === 1 ? v.toFixed(2) : `${v}/${max}`;
  const tone = risk
    ? (v >= 0.6 ? "text-signal" : "text-forest")
    : (v >= 0.7 || (max === 100 && v >= 70) ? "text-forest" : v >= 0.4 || (max === 100 && v >= 40) ? "text-terracotta" : "text-carbon-500");
  return (
    <div className="border border-carbon-200 rounded p-1.5">
      <div className="text-carbon-500">{label}</div>
      <div className={`font-mono ${tone}`}>{display}</div>
    </div>
  );
}
